import { SubscriptionStatus } from "../models";
import type { SystemFeatureCode } from "../models";
import { getStoreUsage } from "../data/store";
import { planRepository, subscriptionRepository } from "../repositories";
import type { SubscriptionOverview } from "../types";
import { planService } from "./plan.service";

function computeDaysRemaining(trialEndsAt?: string, endDate?: string): number | null {
  const target = trialEndsAt ?? endDate;
  if (!target) return null;
  const diff = new Date(target).getTime() - Date.now();
  if (diff <= 0) return 0;
  return Math.ceil(diff / (1000 * 60 * 60 * 24));
}

export const subscriptionService = {
  async getTenantSubscription(tenantId: string): Promise<SubscriptionOverview> {
    const subscription = await subscriptionRepository.findByTenantId(tenantId);
    if (!subscription) {
      throw new Error(`Subscription tenant ${tenantId} introuvable`);
    }
    const plan = await planService.getPlan(subscription.planId);
    const usage = getStoreUsage(tenantId);
    const isTrial = subscription.status === SubscriptionStatus.TRIAL;
    const isExpired =
      subscription.status === SubscriptionStatus.EXPIRED ||
      (isTrial &&
        subscription.trialEndsAt !== undefined &&
        new Date(subscription.trialEndsAt).getTime() <= Date.now());

    const billingAmount =
      subscription.billingCycle === "yearly"
        ? plan.yearlyPrice
        : plan.monthlyPrice;

    return {
      subscription,
      plan,
      usage,
      daysRemaining: computeDaysRemaining(
        subscription.trialEndsAt,
        subscription.endDate
      ),
      isTrial,
      isExpired,
      billingLabel: subscription.billingCycle,
      billingAmount,
    };
  },

  async getCurrentPlan(tenantId: string) {
    const subscription = await subscriptionRepository.findByTenantId(tenantId);
    if (!subscription) throw new Error(`Subscription tenant ${tenantId} introuvable`);
    return planService.getPlan(subscription.planId);
  },

  async isTrial(tenantId: string): Promise<boolean> {
    const subscription = await subscriptionRepository.findByTenantId(tenantId);
    return subscription?.status === SubscriptionStatus.TRIAL;
  },

  async isExpired(tenantId: string): Promise<boolean> {
    const subscription = await subscriptionRepository.findByTenantId(tenantId);
    if (!subscription) return true;
    if (subscription.status === SubscriptionStatus.EXPIRED) return true;
    if (
      subscription.status === SubscriptionStatus.TRIAL &&
      subscription.trialEndsAt &&
      new Date(subscription.trialEndsAt).getTime() <= Date.now()
    ) {
      return true;
    }
    if (
      subscription.endDate &&
      new Date(subscription.endDate).getTime() <= Date.now()
    ) {
      return true;
    }
    return false;
  },

  async daysRemaining(tenantId: string): Promise<number | null> {
    const subscription = await subscriptionRepository.findByTenantId(tenantId);
    if (!subscription) return null;
    return computeDaysRemaining(subscription.trialEndsAt, subscription.endDate);
  },

  async getEnabledFeatures(tenantId: string): Promise<SystemFeatureCode[]> {
    const subscription = await subscriptionRepository.findByTenantId(tenantId);
    return subscription?.features ?? [];
  },

  async hasFeature(
    tenantId: string,
    code: SystemFeatureCode | string
  ): Promise<boolean> {
    const features = await this.getEnabledFeatures(tenantId);
    return features.includes(code as SystemFeatureCode);
  },

  async listSubscriptions() {
    return subscriptionRepository.findAll();
  },
};
